import { PartnerCheckoutReturnInputs } from '../types';

const DEFAULT_CHECKOUT_RETURN_CAMPAIGNS = new Set(['checkout-return']);

/**
 * Whether the visitor is coming back from a partner's own checkout.
 *
 * This is the one and only thing the flow needs to know about the shape of the inbound link. It
 * exists because it is the single case that shows something to a visitor who *already has access* —
 * a partner referral who holds what the partner just sold them is told so, rather than upsold it
 * again — and it is the only such case no `ppt` can express.
 *
 * Everything else about the link is `ppt`, which selects products, and the UTM parameters, which
 * attribute (ADR 0004). `document.referrer` is deliberately never consulted: it is stripped by
 * privacy settings and absent entirely on QR scans and app-to-browser handoffs.
 *
 * The campaign markers are injected rather than hardcoded, for the same reason `ppt` is a closed
 * vocabulary: a partner must not be able to mint one by editing a campaign URL.
 *
 * > This replaced a ten-value `Arrival` union that classified every inbound link by path shape,
 * > `ppt` and `utm_medium`. Only this branch ever changed a decision — products are a pure function
 * > of `ppt` + habitat + user state and never read the arrival — so the other nine names cost a
 * > precedence order, two app-specific path patterns and a large matrix in order to earn an
 * > analytics label. The raw UTM parameters are reported instead, which is strictly more than the
 * > label carried, and the path patterns (`/h/:slug`, `/educators/resources/:slug`) were the only
 * > zl-app-specific code in the otherwise portable core.
 */
const isPartnerCheckoutReturn = ({
  utmSource,
  utmCampaign,
  partnerSources,
  checkoutReturnCampaigns = DEFAULT_CHECKOUT_RETURN_CAMPAIGNS,
}: PartnerCheckoutReturnInputs): boolean =>
  !!utmSource && partnerSources.has(utmSource) && !!utmCampaign && checkoutReturnCampaigns.has(utmCampaign);

export default isPartnerCheckoutReturn;
